import { useState, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import InputText from '../../../components/Input/InputText'
import ErrorText from '../../../components/Typography/ErrorText'
import { showNotification } from "../../common/headerSlice"
import api from "../../../utils/axios"
import { triggerRefresh } from "../../../utils/refreshRegistry"
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline"

const SearchableSelect = ({ meetings, selectedId, onSelect, errorMessage }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = meetings.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedName = selectedId
    ? meetings.find((m) => String(m.id) === String(selectedId))?.name || ""
    : "";

  return (
    <div className={`form-control w-full mt-4 ${isOpen ? "mb-52" : ""}`} ref={ref}>
      <label className="label">
        <span className="label-text text-base-content">Special Meeting Name</span>
      </label>
      <div className="relative">
        <input
          type="text"
          className={`input input-bordered w-full ${errorMessage ? "input-error" : ""}`}
          placeholder="Search and select a meeting..."
          value={isOpen ? search : selectedName}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearch("");
          }}
        />
        {selectedName && !isOpen && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs"
            onClick={() => {
              onSelect("");
              setSearch("");
            }}
          >
            ✕
          </button>
        )}
        {isOpen && (
          <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-base-100 border border-base-300 rounded-lg shadow-lg">
            {filtered.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-primary hover:text-primary-content transition-colors ${
                    String(selectedId) === String(m.id) ? "bg-primary/10 font-semibold" : ""
                  }`}
                  onClick={() => {
                    onSelect(String(m.id));
                    setSearch("");
                    setIsOpen(false);
                  }}
                >
                  {m.name}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-4 py-2 text-sm text-base-content/50">No matches</li>
            )}
          </ul>
        )}
      </div>
      {errorMessage && (
        <label className="label">
          <span className="label-text-alt text-error">{errorMessage}</span>
        </label>
      )}
    </div>
  );
};

async function getMeetingName(meetingId) {
  const response = await api.get(`/api/special-meetings/${meetingId}`);
  return response.data.name;
}

function EditMessageModalBody({closeModal, extraObject}){
  const dispatch = useDispatch()
  const [errors, setErrors] = useState({})
  const [serverError, setServerError] = useState("")
  const [messageObj, setMessageObj] = useState(extraObject)
  const [specialMeetings, setSpecialMeetings] = useState([])

  const isSpecialMessage = !!extraObject.specialMeetingId;

  useEffect(() => {
    if (isSpecialMessage) {
      api.get("api/special-meetings")
        .then((res) => setSpecialMeetings(res.data))
        .catch((err) => console.error("Error fetching special meetings:", err));
    }
  }, [isSpecialMessage]);

  const saveNewMessage = async () => {
    const newErrors = {};
    const objDate = new Date(messageObj.date.trim());
    if (messageObj.date.trim() === "") newErrors.date = "Date is required!";
    else if (objDate.toString() === "Invalid Date") newErrors.date = "Date is invalid!";
    if ((!messageObj.audioUrl || messageObj.audioUrl.trim() === "") && (!messageObj.videoUrl || messageObj.videoUrl.trim() === ""))
      newErrors.audioUrl = "Either an Audio URL or a Video URL is required!";
    if (messageObj.title.trim() === "") newErrors.title = "Title is required!";
    if (messageObj.speaker.trim() === "") newErrors.speaker = "Speaker is required!";
    if (isSpecialMessage && !messageObj.specialMeetingId) newErrors.specialMeetingId = "Special Meeting Name is required!";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const newMessageObj = {
      date: messageObj.date,
      title: messageObj.title,
      speaker: messageObj.speaker,
      audioUrl: messageObj.audioUrl,
      videoUrl: messageObj.videoUrl,
    };

    if (isSpecialMessage) {
      newMessageObj.specialMeetingId = parseInt(messageObj.specialMeetingId);
      newMessageObj.specialMeetingName = await getMeetingName(parseInt(messageObj.specialMeetingId));
    }

    const messageId = messageObj.id;
    const apiEndpoint = isSpecialMessage
      ? `api/special-meeting-messages/${messageId}`
      : `api/messages/${messageId}`;

    try {
      await api.put(apiEndpoint, newMessageObj);
      
      dispatch(showNotification({message : "Message Edited!", status : 1}));
      closeModal();

      if (isSpecialMessage) {
        triggerRefresh("specialMessages");
      } else {
        triggerRefresh("messages");
      }
    } catch(error) {
      console.error("Error editing message:", error);

      const errorMsg = error.response?.data?.message || "Failed to edit message. Please try again.";
      setServerError(errorMsg);
      
      dispatch(showNotification({ message: errorMsg, status: 0 }));
    }
  }

  const updateFormValue = ({updateType, value}) => {
    setErrors((prev) => ({ ...prev, [updateType]: "" }))
    setServerError("")
    setMessageObj({...messageObj, [updateType] : value})
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(showNotification({ message: "URL copied to clipboard!", status: 1 }));
    } catch (err) {
      console.error("Failed to copy:", err);
      dispatch(showNotification({ message: "Failed to copy URL", status: 0 }));
    }
  }

  return (
    <>
      <InputText type="date" defaultValue={messageObj.date.slice(0, 10)} updateType="date" containerStyle="mt-4" labelTitle="Date" updateFormValue={updateFormValue} disabled={true} errorMessage={errors.date}/>
      <InputText type="text" defaultValue={messageObj.title} updateType="title" containerStyle="mt-4" labelTitle="Title" updateFormValue={updateFormValue} errorMessage={errors.title}/>
      <InputText type="text" defaultValue={messageObj.speaker} updateType="speaker" containerStyle="mt-4" labelTitle="Speaker" updateFormValue={updateFormValue} errorMessage={errors.speaker}/>

      <div className="form-control w-full mt-4">
        <label className="label">
          <span className="label-text text-base-content">
            Audio URL
          </span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="url"
            value={messageObj.audioUrl || ""}
            disabled={true}
            className={`input input-bordered w-full ${errors.audioUrl ? "input-error" : ""}`}
            readOnly
          />
          <button
            type="button"
            onClick={() => copyToClipboard(messageObj.audioUrl)}
            className="btn btn-ghost btn-square"
            title="Copy URL to clipboard"
          >
            <ClipboardDocumentIcon className="w-5 h-5" />
          </button>
        </div>
        {errors.audioUrl && (
          <label className="label">
            <span className="label-text-alt text-error">{errors.audioUrl}</span>
          </label>
        )}
      </div>
      <InputText type="text" defaultValue={messageObj.videoUrl} updateType="videoUrl" containerStyle="mt-4" labelTitle="Video Url" updateFormValue={updateFormValue}/>

      {isSpecialMessage && (
        <SearchableSelect
          meetings={specialMeetings}
          selectedId={messageObj.specialMeetingId}
          onSelect={(value) => updateFormValue({ updateType: "specialMeetingId", value })}
          errorMessage={errors.specialMeetingId}
        />
      )}

      {serverError && <ErrorText styleClass="mt-4">{serverError}</ErrorText>}

      <div className="modal-action">
          <button  className="btn btn-ghost" onClick={() => closeModal()}>Cancel</button>
          <button  className="btn btn-primary px-6" onClick={() => saveNewMessage()}>Save</button>
      </div>
    </>
  )
}

export default EditMessageModalBody
